import { Upload } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { Checkbox } from "@/components/ui/checkbox";
import { CreativeEditorSection } from "./CreativeEditorDialog";
import { PrivateImage } from "./PrivateImage";
import { PrivateReferenceRow } from "./PrivateReferenceRow";
import { UnavailableReferenceRow } from "./UnavailableReferenceRow";
import { CREATIVE_REFERENCE_LIMIT } from "@digital-twins/shared-types";
import type { BrandMaterialsProps } from "@/types/creative-library";

export function BrandMaterials({
  creatives,
  selected,
  unavailable = [],
  privateTokens = [],
  onChange,
  onUploadLogo,
}: BrandMaterialsProps) {
  const { t } = useTranslation("app");
  return (
    <CreativeEditorSection>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>{t("creativeLibrary.approvedMaterials")}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={onUploadLogo}
          disabled={selected.length >= CREATIVE_REFERENCE_LIMIT}
          data-testid="creative-brand-upload-logo"
        >
          <Upload className="h-4 w-4" />
          {t("creativeLibrary.uploadLogo")}
        </Button>
      </div>
      <Text size="xs" color="muted">
        {t("creativeLibrary.brandLogoHelp")}
      </Text>
      {(creatives.length > 0 ||
        unavailable.length > 0 ||
        privateTokens.length > 0) && (
        <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
          {unavailable.map((token) => (
            <UnavailableReferenceRow
              key={token}
              token={token}
              onRemove={() =>
                onChange(selected.filter((item) => item !== token))
              }
            />
          ))}
          {privateTokens.map((token) => (
            <PrivateReferenceRow key={token} token={token} />
          ))}
          {creatives.map((item) => (
            <label
              key={item.token}
              className="flex min-h-11 items-center gap-3 rounded-lg border border-border p-2 text-sm"
            >
              <Checkbox
                checked={selected.includes(item.token)}
                disabled={
                  !selected.includes(item.token) &&
                  selected.length >= CREATIVE_REFERENCE_LIMIT
                }
                onCheckedChange={(checked) =>
                  onChange(
                    checked === true
                      ? [...selected, item.token]
                      : selected.filter((token) => token !== item.token),
                  )
                }
              />
              {item.role === "logo" && (
                <PrivateImage
                  source={item.mediaUrl}
                  alt={item.title}
                  className="h-10 w-16 shrink-0 object-contain"
                />
              )}
              <span className="min-w-0 break-words">{item.title}</span>
            </label>
          ))}
        </div>
      )}
    </CreativeEditorSection>
  );
}
